import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Linkedin, Instagram } from 'lucide-react';
import Logo from './Logo';

// Custom TikTok icon since lucide doesn't have it
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/terangagloballegal' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/terangagloballegal' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/terangagloballegal' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://tiktok.com/@terangagloballegal' },
    { name: 'WhatsApp', icon: WhatsAppIcon, url: 'https://wa.me/221776984952' },
  ];

  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Logo variant="light" />
            <p className="text-slate-400 leading-relaxed">
              Votre partenaire pour réussir. Expertise juridique et accompagnement entrepreneurial au Sénégal.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a 
                  key={social.name}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:bg-[#E6C200] hover:text-[#1A1A1A] transition-all" 
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="footer-link">Accueil</Link>
              </li>
              <li>
                <Link to="/a-propos" className="footer-link">À propos</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Services</Link>
              </li>
              <li>
                <Link to="/les-3f" className="footer-link">Les 3F</Link>
              </li>
              <li>
                <Link to="/blog" className="footer-link">Blog</Link>
              </li>
              <li>
                <Link to="/contact" className="footer-link">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Nos Services</h4>
            <ul className="space-y-3">
              <li>
                <span className="text-slate-400">Conseil Juridique</span>
              </li>
              <li>
                <span className="text-slate-400">Création d'Entreprise</span>
              </li>
              <li>
                <span className="text-slate-400">Formation Entrepreneuriale</span>
              </li>
              <li>
                <span className="text-slate-400">Accompagnement Financement</span>
              </li>
              <li>
                <span className="text-slate-400">Études en France / Canada</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#E6C200] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">Commune de Keur Moussa/Tougouny<br />Thiès, Sénégal</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#E6C200] flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <a href="tel:+33758709661" className="footer-link">+33 7 58 70 96 61</a>
                  <a href="tel:+221776984952" className="footer-link">+221 77 698 49 52</a>
                  <a href="tel:+221769326506" className="footer-link">+221 76 932 65 06</a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#E6C200] flex-shrink-0" />
                <a href="mailto:contact@terangaglobal.sn" className="footer-link">contact@terangaglobal.sn</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Teranga Global Legal. Tous droits réservés.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="text-slate-500 hover:text-white transition-colors">
              Politique de confidentialité
            </a>
            <a href="#" className="text-slate-500 hover:text-white transition-colors">
              Conditions d'utilisation
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
